// SearchOverlay — global search (Epic 4 · S4.4).
//
// fn_work_search(p_q, p_limit) searches work AND conversation, five years,
// permission-filtered in SQL (the walls apply through v_my_messages). Day one
// nobody knows where anything is — the fastest path to anything is typing what
// you remember. Search is a last resort: if someone has to search for a standing
// fact about a client, the slide-out failed to put it in front of them.
//
// Binds `/` (wired in ShellSurfaces) — the same gesture everywhere. Esc closes.
// Results render defensively (UNCONFIRMED shape); a gated result never reaches
// the UI because the RPC filters server-side. Clicking a conversation/thread
// result opens the inbox drawer at that thread; other results are shown with
// their metadata so the person knows where to go.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { workSearch } from '../../../api/shell';
import { useShellSurface } from '../../../contexts/ShellSurfaceContext';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import { formatWhen } from '../../shared/format';

const normResult = (r = {}) => ({
  id: r.id ?? r.uuid ?? null,
  kind: r.kind ?? r.type ?? r.result_type ?? null,
  threadId: r.thread_id ?? r.thread ?? null,
  taskId: r.task_id ?? r.task ?? null,
  clientId: r.client_id ?? r.client ?? null,
  clientName: r.client_name ?? r.client ?? null,
  title: r.title ?? r.subject ?? r.name ?? null,
  snippet: r.snippet ?? r.preview ?? r.body ?? r.summary ?? null,
  at: r.at ?? r.when ?? r.created_at ?? r.date ?? null,
});

const kindLabel = (k) => {
  if (!k) return 'Result';
  const map = {
    work: 'Work',
    task: 'Work',
    conversation: 'Conversation',
    thread: 'Conversation',
    message: 'Message',
    client: 'Client',
  };
  return map[k] || k;
};

export default function SearchOverlay() {
  const { search: open, closeSearch, openInbox } = useShellSurface();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus the input on open; clear on close.
  // Focus the input on open; clear state. The input is mounted by the time this
  // effect runs (open just flipped false→true), so focus it directly.
  useEffect(() => {
    if (open) {
      setQ('');
      setResults([]);
      setError(null);
      inputRef.current?.focus();
    }
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeSearch]);

  const run = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await workSearch(query.trim(), 40);
      setResults((res?.data || []).map(normResult));
    } catch (err) {
      setError(err?.message || "Couldn't search.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce — search fires after the person stops typing for 250ms.
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => run(q), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, open, run]);

  if (!open) return null;

  const onPick = (r) => {
    if (r.threadId) openInbox(r.threadId);
    else closeSearch();
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Search">
      <div className="absolute inset-0 bg-black/40" onClick={closeSearch} aria-hidden="true" />
      <div
        className="absolute inset-x-0 top-0 mx-auto w-full max-w-2xl bg-white shadow-xl dark:bg-gray-900 md:top-20 md:rounded-2xl"
        data-testid="search-overlay"
      >
        <div className="flex items-center gap-3 border-b border-gray-200 p-3 dark:border-gray-800">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search work and conversations… (Esc to close)"
            className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100"
            aria-label="Search query"
            data-testid="search-input"
          />
          <button
            type="button"
            onClick={closeSearch}
            className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Esc
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {error ? (
            <div className="p-2"><ErrorState variant="strip" message={error} /></div>
          ) : loading ? (
            <div className="p-4 text-sm text-gray-400">Searching…</div>
          ) : q.trim().length < 2 ? (
            <div className="p-4"><EmptyState variant="inset" message="Type at least two characters to search across five years of work and conversations." padding="p-6" /></div>
          ) : results.length === 0 ? (
            <div className="p-4"><EmptyState variant="inset" message={`No results for “${q}”.`} padding="p-6" /></div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="search-results">
              {results.map((r, i) => (
                <li key={r.id || i}>
                  <button
                    type="button"
                    onClick={() => onPick(r)}
                    className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <span className="mt-0.5 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      {kindLabel(r.kind)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {r.title || r.snippet || 'Untitled'}
                        </span>
                        {r.clientName && (
                          <span className="ml-auto shrink-0 text-xs text-gray-400">{r.clientName}</span>
                        )}
                      </span>
                      {r.snippet && r.title && (
                        <span className="mt-0.5 block truncate text-sm text-gray-500 dark:text-gray-400">{r.snippet}</span>
                      )}
                      {r.at && (
                        <span className="mt-0.5 block text-xs text-gray-400 tabular-nums">{formatWhen(r.at)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
